import { useMemo, useState } from "react";

import { makeStyles, Table, TableHead, TableRow } from "@material-ui/core";

import RouteLeavingGuard from "src/components/RouteLeavingGuard";
import { CampaignWithCalleeFields_calleeFields } from "src/graphql/generated/CampaignWithCalleeFields";
import { getCampaign_campaign } from "src/graphql/generated/getCampaign";
import { CalleeField } from "src/graphql/generated/globalTypes";
import {
  fieldIsAlwaysRequired,
  questionsContainField
} from "src/util/calleeFields";

import CalleeFieldTableRow, {
  ControlsCell,
  MappingCell,
  NameCell
} from "./CalleeFieldTableRow";
import NewCalleeFieldTableRow from "./NewCalleeFieldTableRow";

const useStyles = makeStyles(() => ({
  table: {
    tableLayout: "fixed"
  }
}));

const fieldKey = (calleeField: CampaignWithCalleeFields_calleeFields): string =>
  (calleeField.id || calleeField.builtInField)!;

function sortCalleeFields(
  calleeFields: CampaignWithCalleeFields_calleeFields[]
): CampaignWithCalleeFields_calleeFields[] {
  // Required fields come first, followed by mapped fields, followed by unmapped fields
  // Each group is sorted alphabetically by name

  const requiredFields: CampaignWithCalleeFields_calleeFields[] = [];
  const mappedFields: CampaignWithCalleeFields_calleeFields[] = [];
  const unmappedFields: CampaignWithCalleeFields_calleeFields[] = [];

  calleeFields.forEach(field => {
    if (fieldIsAlwaysRequired(field)) {
      requiredFields.push(field);
    } else if (field.dataSourceFieldName) {
      mappedFields.push(field);
    } else {
      unmappedFields.push(field);
    }
  });

  return [
    ...requiredFields.sort((a, b) => a.name.localeCompare(b.name)),
    ...mappedFields.sort((a, b) => a.name.localeCompare(b.name)),
    ...unmappedFields.sort((a, b) => a.name.localeCompare(b.name))
  ];
}

export default function CalleeFields({
  calleeFields,
  onAddField,
  onEditField,
  onDeleteField,
  dataSourceFieldNames,
  campaign,
  dataSourceFieldLabel
}: {
  calleeFields: CampaignWithCalleeFields_calleeFields[];
  onAddField: (
    name: string,
    dataSourceFieldName: string | null,
    builtInFieldName?: CalleeField | null
  ) => any;
  onEditField: (
    fieldId: string,
    name: string,
    dataSourceFieldName: string | null
  ) => any;
  onDeleteField: (fieldId: string) => any;
  dataSourceFieldNames: string[];
  campaign: getCampaign_campaign;
  dataSourceFieldLabel: string;
}): JSX.Element {
  const [hasUnsavedNewCalleeField, setHasUnsavedNewCalleeField] =
    useState(false);
  const [calleeFieldsWithUnsavedChanges, setCalleeFieldsWithUnsavedChanges] =
    useState(
      calleeFields.reduce((fieldStates, field) => {
        return {
          ...fieldStates,
          [fieldKey(field)]: false
        };
      }, {})
    );

  const [
    hasTriedToLeaveWithUnsavedChanges,
    setHasTriedToLeaveWithUnsavedChanges
  ] = useState(false);

  const classes = useStyles();

  const handleDirtyMap = useMemo(() => {
    const handlers: { [key: string]: (dirty: boolean) => void } = {};
    for (const cf of calleeFields) {
      const key = fieldKey(cf);
      handlers[key] = (dirty: boolean) =>
        setCalleeFieldsWithUnsavedChanges(current => ({
          ...current,
          [key]: dirty
        }));
    }
    return handlers;
  }, [calleeFields]);

  const renderCalleeField = (
    calleeField: CampaignWithCalleeFields_calleeFields
  ) => {
    const key = fieldKey(calleeField);

    let required: false | { reason: string } = false;
    if (fieldIsAlwaysRequired(calleeField)) {
      required = { reason: "this field is required for all campaigns" };
    } else if (questionsContainField(campaign.questions, calleeField)) {
      required = { reason: "this field is used in a question" };
    }

    const handleEditField = async (
      name: string,
      dataSourceFieldName: string | null
    ) => {
      if (calleeField.id) {
        // custom field, or edited built-in-field
        await onEditField(calleeField.id!, name, dataSourceFieldName);
      } else {
        // built-in field
        await onAddField(name, dataSourceFieldName, calleeField.builtInField);
      }
      handleDirtyMap[key](false);
    };

    const handleDeleteField = async () => {
      if (calleeField.id) {
        await onDeleteField(calleeField.id);
        handleDirtyMap[key](false);
      }
    };

    return (
      <CalleeFieldTableRow
        key={key}
        calleeField={calleeField}
        onEdit={handleEditField}
        onDelete={handleDeleteField}
        dataSourceFieldNames={dataSourceFieldNames}
        onDirty={handleDirtyMap[key]}
        highlightUnsavedChanges={hasTriedToLeaveWithUnsavedChanges}
        required={required}
        campaign={campaign}
        dataSourceFieldLabel={dataSourceFieldLabel}
      />
    );
  };

  return (
    <>
      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            <ControlsCell />
            <NameCell>Name</NameCell>
            <MappingCell>Map to {dataSourceFieldLabel}</MappingCell>
          </TableRow>
        </TableHead>
        <NewCalleeFieldTableRow
          dataSourceFieldNames={dataSourceFieldNames}
          onSubmit={onAddField}
          onDirty={setHasUnsavedNewCalleeField}
          highlightUnsavedChanges={hasTriedToLeaveWithUnsavedChanges}
          dataSourceFieldLabel={dataSourceFieldLabel}
        />
        {sortCalleeFields(calleeFields).map(renderCalleeField)}
      </Table>
      <RouteLeavingGuard
        when={
          hasUnsavedNewCalleeField ||
          Object.values(calleeFieldsWithUnsavedChanges).some(x => x)
        }
        onCancel={() => setHasTriedToLeaveWithUnsavedChanges(true)}
      />
    </>
  );
}
